/**
 * NewFacilityModal — quick-create dialog for a facility (iter 170 split).
 *
 * On success, calls ``onCreated(facilityId)`` so the parent can close the
 * modal, refresh the list, and open the new facility's drawer.
 */
import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, X } from 'lucide-react';
import { Button } from '../ui/button';
import { TextField } from './uiKit';
import { API } from './formatters';

export function NewFacilityModal({ onClose, onCreated, getAuthHeader }) {
  const [name, setName] = useState('');
  const [npi, setNpi] = useState('');
  const [phone, setPhone] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (name.trim().length < 1) return toast.error('Name required');
    setBusy(true);
    try {
      const { data } = await axios.post(
        `${API}/crm/facilities`,
        { name: name.trim(), npi: npi || null, phone: phone || null },
        getAuthHeader(),
      );
      onCreated(data.id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not create facility');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[70] flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="crm-new-facility-modal"
    >
      <div
        className="bg-slate-950 border border-slate-800 rounded-2xl w-full max-w-md"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="p-5 border-b border-slate-800 flex items-center justify-between">
          <h2 className="text-lg font-semibold">New facility</h2>
          <button onClick={onClose} data-testid="crm-new-facility-close">
            <X className="h-4 w-4 text-slate-500" />
          </button>
        </header>
        <div className="p-5 space-y-3">
          <TextField label="Name *" value={name}  onChange={setName}  testId="crm-new-name" autoFocus />
          <TextField label="NPI"    value={npi}   onChange={setNpi}   testId="crm-new-npi" />
          <TextField label="Phone"  value={phone} onChange={setPhone} testId="crm-new-phone" />
        </div>
        <footer className="p-5 border-t border-slate-800 flex items-center justify-end gap-2">
          <Button variant="outline" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button
            onClick={submit}
            disabled={busy || !name.trim()}
            data-testid="crm-new-facility-submit"
          >
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Create'}
          </Button>
        </footer>
      </div>
    </div>
  );
}

export default NewFacilityModal;
